// The two questions an online opponent can put to us mid-game — a draw offer
// and a restart proposal. Each is a modal that the wire can also take away
// again (the offer lapsed, the proposal was cancelled), which is why these
// hold a handle instead of using `confirmDialog`.
import { openModal, type ModalHandle } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

export interface PromptCopy {
  readonly title: string;
  readonly body: string;
  readonly accept: string;
  readonly decline: string;
}

export class OpponentPrompt {
  private modal: ModalHandle | null = null;

  constructor(private readonly copy: (name: string) => PromptCopy) {}

  /** Ask; `answer` runs once, with the choice. Replaces a prompt already open. */
  open(opponentName: string, answer: (accept: boolean) => void): void {
    this.close();
    const copy = this.copy(opponentName);
    const settle = (accept: boolean): void => {
      this.close();
      answer(accept);
    };
    const modal = openModal({
      title: copy.title,
      body: copy.body,
      actions: [
        { label: copy.accept, primary: true, onClick: () => settle(true) },
        { label: copy.decline, onClick: () => settle(false) },
      ],
      dismissible: false,
    });
    this.modal = modal;
    modal.focusPrimary();
  }

  close(): void {
    this.modal?.close();
    this.modal = null;
  }
}

export function drawPrompt(): OpponentPrompt {
  return new OpponentPrompt((name) => ({
    title: t('online.draw.offeredTitle'),
    body: t('online.draw.offeredBody', { name }),
    accept: t('game.draw.accept'),
    decline: t('game.draw.decline'),
  }));
}

export function restartPrompt(): OpponentPrompt {
  return new OpponentPrompt((name) => ({
    title: t('online.restart.title'),
    body: t('online.restart.body', { name }),
    accept: t('online.restart.accept'),
    decline: t('game.draw.decline'),
  }));
}
